import {
  type ProductSettings,
  useProductSettings,
} from '@workspace/utils-react';
import type { ReactNode } from 'react';
import { useEffect } from 'react';
import { AuthProvider } from '../../../asset-manager/src/providers/AuthProvider';
import useIdentity from '../useIdentity';

interface AuthBridgeProviderProps {
  instanceId?: string;
  children: ReactNode;
}

export function AuthBridgeProvider({
  instanceId,
  children,
}: AuthBridgeProviderProps): React.ReactElement {
  const { getAccessToken, signin, loading, error, isAuthenticated, user } =
    useIdentity();
  const settings = useProductSettings<ProductSettings>();

  useEffect(() => {
    if (!isAuthenticated && !loading && !error) {
      signin();
    }
  }, [isAuthenticated, loading, error, signin]);

  useEffect(() => {
    if (
      error &&
      (error as any)?.message === 'login_required' &&
      settings?.workspaceUrl
    ) {
      const message = {
        type: 'RequestReauthentication',
      };
      window.parent.postMessage(JSON.stringify(message), settings.workspaceUrl);
    }
  }, [error, settings?.workspaceUrl]);

  if (loading) {
    return (
      <div className="loading-container">
        <p>Loading authentication...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <h2>Authentication Error: {error?.message}</h2>
      </div>
    );
  }

  if (isAuthenticated && user) {
    const email = user.profile?.email as string | undefined;
    const organisationId = user.profile?.organisationId as string | undefined;
    return (
      <AuthProvider
        getAccessToken={getAccessToken}
        instanceId={instanceId}
        email={email}
        organisationId={organisationId}
      >
        {children}
      </AuthProvider>
    );
  }

  return (
    <div className="unauthenticated-container">
      <p>Authentication required...</p>
    </div>
  );
}
